import { TransactionListItem } from "@common/schemas/transaction.schema";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import {
    ReviewCellVariant,
    ReviewCellVariantSchema,
    ReviewColumnMetaSchema,
} from "@common/types/views/transactionViews";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import classNames from "classnames";
import {
    memo,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from "react";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { getMatchedDocumentFieldLock } from "@common/helpers/matched-document-transaction-fields";
import {
    useIsReadonlyOrLocked,
    useIsTableReadonly,
} from "@hooks/useIsReadonlyOrLocked";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useUser } from "@hooks/useUser";
import { Picker } from "@design-system/Picker/Picker.lib";
import { ClassAvatarGroup } from "@components/avatar/ClassAvatarGroup";
import { useClasses } from "@api/class.api";
import { ClassWithChildren } from "@helpers/class";
import { addTransactionFieldReviews } from "@api/transaction.api";
import { ClassCombobox } from "../TransactionSplitV2/ClassCombobox";
import { getClassesAriaLabel } from "../TransactionSplitV2/getClassesAriaLabel";
import { PaginatedTransactionsActionsContext } from "../PaginatedTransactionsTable.context";
import { updateTransactionInCache } from "../useTransactionsQuery";
import { transactionCellAreEqual } from "./transactionCellComparators";
import {
    isTransactionFieldReviewedByUser,
    mergeTransactionFieldReviews,
} from "./transactionFieldReviewUtils";
import { TransactionFieldReviewButton } from "./TransactionFieldReviewButton";
import { useOptimisticClassIds } from "./useOptimisticClassIds";
import styles from "./ClassesCell.module.scss";

export const ClassesCell = memo(
    ({
        row,
        table,
        column,
    }: CellContext<ViewTableFeatures, TransactionListItem, any>) => {
        const transaction = row.original;
        const isReadonlyOrLocked = useIsReadonlyOrLocked(row.original, table);
        const isTableReadonly = useIsTableReadonly(table);
        const { isLocked: isMatchLocked, tooltip: matchLockTooltip } =
            getMatchedDocumentFieldLock({
                matchLists: [
                    row.original.transactionMatches,
                    row.original.accrualMatches,
                ],
                isOtherwiseLocked: isReadonlyOrLocked,
            });
        const isDisabled = isReadonlyOrLocked || isMatchLocked;
        const [isOpen, setIsOpen] = useState(false);
        const user = useUser();
        const { error: errorToast } = useToaster();

        const { saveTransaction } = useContext(
            PaginatedTransactionsActionsContext,
        );

        const metaParse = ReviewColumnMetaSchema.safeParse(
            column.columnDef.meta,
        );
        const rawVariant =
            metaParse.data?.customVariants?.variant ??
            metaParse.data?.variant ??
            "full";
        const variantParse = ReviewCellVariantSchema.safeParse(rawVariant);
        const variant: ReviewCellVariant = variantParse.success
            ? variantParse.data
            : "full";
        const isReviewVariant = variant === "review";

        // useClasses() is also called inside ClassCombobox for its own value/list state.
        // This separate call is needed here to build a sorted selectedClasses list
        // for the trigger's ClassAvatarGroup display. React Query deduplicates the fetch.
        const { classesMap, sortedClassesIndexMap } = useClasses();

        // Derive classes from transaction data
        const derivedClasses = useMemo(
            () => transaction.classAssignments?.map((c) => c.classId) ?? [],
            [transaction.classAssignments],
        );

        // While a save is in flight the checkbox and trigger render the pick
        // instead of the (not-yet-updated) server-derived classes.
        const {
            classIds: effectiveClassIds,
            begin: beginOptimisticClassIds,
            settle: settleOptimisticClassIds,
        } = useOptimisticClassIds({
            serverClassIds: derivedClasses,
            rowAssignments: transaction.classAssignments,
        });
        const pendingClassIdsRef = useRef<string[] | null>(null);
        const isSavingRef = useRef(false);

        const selectedClasses = useMemo(() => {
            if (!effectiveClassIds.length) {
                return [];
            }
            return effectiveClassIds
                .map((id) => classesMap.get(id))
                .filter((c): c is ClassWithChildren => Boolean(c))
                .toSorted((a, b) => {
                    const aIdx = sortedClassesIndexMap.get(a.id) ?? Infinity;
                    const bIdx = sortedClassesIndexMap.get(b.id) ?? Infinity;
                    return aIdx - bIdx;
                });
        }, [effectiveClassIds, classesMap, sortedClassesIndexMap]);

        const isReviewed = useMemo(
            () =>
                isTransactionFieldReviewedByUser(
                    row.original.fieldReviews,
                    TransactionReviewField.CLASS,
                    user?.id,
                ),
            [row.original.fieldReviews, user?.id],
        );

        const markClassReviewedIfNeeded = useCallback(async () => {
            // In review variant, automatically mark the class field as
            // reviewed after a successful change. The class change has
            // already committed, so surface a toast rather than letting a
            // failed review mark fail silently.
            if (!isReviewVariant) {
                return;
            }
            try {
                const reviews = await addTransactionFieldReviews(
                    transaction.id,
                    [TransactionReviewField.CLASS],
                );
                updateTransactionInCache(transaction.id, (t) => ({
                    ...t,
                    fieldReviews: mergeTransactionFieldReviews(
                        t.fieldReviews,
                        reviews,
                    ),
                }));
            } catch {
                errorToast(
                    "Failed to mark class as reviewed. Please try again.",
                );
            }
        }, [isReviewVariant, transaction.id, errorToast]);

        const handleClassesChange = useCallback(
            async (classIds: string[]) => {
                if (isDisabled) {
                    return;
                }
                beginOptimisticClassIds(classIds);
                pendingClassIdsRef.current = classIds;
                if (isSavingRef.current) {
                    return; // the running loop below picks up the latest set
                }
                isSavingRef.current = true;
                // Rollback base for the next PATCH: advance it to each
                // successfully-persisted transaction so that if a later PATCH
                // fails, saveTransaction rolls back to the last saved set
                // rather than the original pre-edit snapshot — otherwise an
                // earlier persisted set would be discarded and the cell would
                // diverge from the server.
                let baseTransaction = transaction;
                let didSucceed = false;
                const saveLatestClassIds = async () => {
                    // Saves are serialized and coalesced: at most one PATCH is
                    // in flight, and sets clicked while a save runs collapse
                    // into the single latest set, always persisted last.
                    while (pendingClassIdsRef.current) {
                        const nextClassIds = pendingClassIdsRef.current;
                        pendingClassIdsRef.current = null;
                        const saved = await saveTransaction(baseTransaction, {
                            classIds: nextClassIds,
                        });
                        didSucceed = saved !== undefined;
                        if (saved) {
                            baseTransaction = saved;
                        }
                        await markClassReviewedIfNeeded();
                    }
                };
                await saveLatestClassIds().finally(() => {
                    isSavingRef.current = false;
                    pendingClassIdsRef.current = null;
                    // Server truth (the cache upsert, or the rollback on error)
                    // takes over once it reaches the row.
                    settleOptimisticClassIds({ didSucceed });
                });
            },
            [
                isDisabled,
                beginOptimisticClassIds,
                settleOptimisticClassIds,
                saveTransaction,
                transaction,
                markClassReviewedIfNeeded,
            ],
        );

        const triggerContent = useMemo(() => {
            if (selectedClasses.length === 0) {
                return null;
            }
            return <ClassAvatarGroup classes={selectedClasses} max={3} />;
        }, [selectedClasses]);

        const trigger = useMemo(
            () => (
                <Picker.CellContent
                    readOnly={isDisabled}
                    buttonProps={{ tooltip: matchLockTooltip }}
                    showDisabledAdd={!!matchLockTooltip}
                    className={classNames({
                        [styles.reviewVariant]: isReviewVariant,
                        [styles.isReviewed]: isReviewVariant && isReviewed,
                    })}
                    value={triggerContent ?? undefined}
                    aria-label={getClassesAriaLabel(selectedClasses)}
                />
            ),
            [
                isDisabled,
                triggerContent,
                selectedClasses,
                isReviewVariant,
                isReviewed,
                matchLockTooltip,
            ],
        );

        return (
            <ViewTableCell captureClicks className={styles.classesCell}>
                <ClassCombobox
                    selectedClassIds={effectiveClassIds}
                    onChange={handleClassesChange}
                    disabled={isDisabled}
                    hideArrow
                    open={isOpen}
                    onOpenChange={setIsOpen}
                    trigger={trigger}
                />

                {isReviewVariant && (
                    <TransactionFieldReviewButton
                        transaction={row.original}
                        isReadonly={isTableReadonly}
                        field={TransactionReviewField.CLASS}
                        reviewTooltip="Review class"
                    />
                )}
            </ViewTableCell>
        );
    },
    transactionCellAreEqual,
);
ClassesCell.displayName = "ClassesCell";
